import { Component, type ReactNode } from 'react';
import { useLocation, useRoutes } from 'react-router-dom';

import { AppRoutes } from './appRoutes';

import { useMixpanelRouteTracking } from '@/hooks/useMixpanelRouteTracking';
import { captureException } from '@/utils/errorMonitoring';

type RouteErrorBoundaryProps = {
  children: ReactNode;
  resetKey: string;
};

type RouteErrorBoundaryState = {
  hasError: boolean;
};

class RouteErrorBoundary extends Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  static getDerivedStateFromError() {
    return { hasError: true };
  }

  constructor(props: RouteErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  override componentDidUpdate(previousProps: RouteErrorBoundaryProps) {
    const { hasError } = this.state;
    const { resetKey } = this.props;

    if (hasError && previousProps.resetKey !== resetKey) {
      this.setState({ hasError: false });
    }
  }

  override componentDidCatch(error: Error) {
    captureException(error, {
      fingerprint: ['route_render_error'],
      tags: { route_error_boundary: true },
    });
  }

  override render() {
    const { children } = this.props;
    const { hasError } = this.state;

    if (hasError) {
      return (
        <main className="flex min-h-screen items-center justify-center bg-sage-100 px-6 text-center">
          <section className="max-w-md rounded-lg bg-white p-8 shadow-sm">
            <h1 className="text-midnight-900 text-2xl font-semibold">
              Something went wrong while loading this page.
            </h1>
            <p className="text-midnight-700 mt-3 text-base">
              Refresh the page to try again. If this keeps happening, contact
              support.
            </p>
            <button
              className="bg-midnight-900 mt-6 rounded-full px-5 py-2 text-sm font-semibold text-white"
              type="button"
              onClick={() => window.location.reload()}
            >
              Refresh page
            </button>
          </section>
        </main>
      );
    }

    return children;
  }
}

const AppRouter = () => {
  useMixpanelRouteTracking(AppRoutes);
  const location = useLocation();

  return (
    <RouteErrorBoundary resetKey={location.key}>
      {useRoutes(AppRoutes)}
    </RouteErrorBoundary>
  );
};

export default AppRouter;
